import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Segment } from "semantic-ui-react";
import { t } from "i18next";
import useTheme from "../../../hooks/useTheme";
import { AppColumn } from "../../UI/Layouts";
import AppText from "../../UI/Texts/AppTexts";
import AppFooter from "../../Footers/AppFooter";
import RecruiterCard from "./Components/RecruiterCard";
import { fetchAndSetRecruitersList } from "../../../redux/Recruiters/recruitersActions";
import "./RecruiterList.css";

const RecruiterListPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const recruitersState = useSelector((state) => state.recruiters) ?? {};
  const recruiters = recruitersState.recruitersList ?? [];
  const isLoading = recruitersState.recruitersListLoading ?? true;

  const isSSR = typeof window === "undefined";

  useEffect(() => {
    // Keep the cached SSR list as the first paint, then refresh editable
    // recruiter fields from the public API.
    dispatch(fetchAndSetRecruitersList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let content;
  if (isLoading && !isSSR && recruiters.length === 0) {
    content = (
      <Segment
        basic
        loading
        placeholder
        style={{ height: "80vh", width: "100%" }}
      />
    );
  } else if (recruiters.length === 0) {
    content = (
      <AppColumn
        mainAxisAlignment="center"
        crossAxisAlignment="center"
        style={{
          padding: theme.spacing.contentElementPadding,
          height: "60vh",
          width: "100%",
        }}
      >
        <AppText.BodyText center color={theme.colors.grey}>
          {t("recruiters.list.empty")}
        </AppText.BodyText>
      </AppColumn>
    );
  } else {
    content = (
      <main
        className="recruiter-list-page"
        style={{
          "--recruiter-list-top": theme.recruiterDirectory.pageTopPadding,
          "--recruiter-list-bottom": theme.recruiterDirectory.pageBottomPadding,
          "--recruiter-list-gutter": theme.spacing.contentElementPadding,
          "--recruiter-list-width": theme.recruiterDirectory.maxWidth,
          "--recruiter-list-header-width": theme.recruiterDirectory.headerMaxWidth,
          "--recruiter-list-grid-gap": theme.recruiterDirectory.gridGap,
          "--recruiter-list-primary": theme.primaryColor,
          "--recruiter-list-text": theme.colors.dark,
          "--recruiter-list-muted": theme.colors.textSecondary,
          "--recruiter-list-background": theme.colors.neutralBg,
          "--recruiter-list-tint": `color-mix(in srgb, ${theme.primaryColor} 8%, ${theme.colors.white})`,
        }}
      >
        <div className="recruiter-list-page__inner">
          <header className="recruiter-list-page__header">
            <span className="recruiter-list-page__eyebrow">
              {t("recruiters.list.eyebrow", {appName: theme.appName})}
            </span>
            <AppText.HeadingBig use="h1" className="recruiter-list-page__title">
              {t("recruiters.list.title")}
            </AppText.HeadingBig>
            <AppText.BodyText className="recruiter-list-page__description">
              {theme.description || theme.tagline ||
                t("recruiters.list.description", {appName: theme.appName})}
            </AppText.BodyText>
          </header>
          <Grid className="recruiter-list-page__grid" stackable columns={2}>
            {recruiters.map((recruiter) => (
              <Grid.Column
                key={recruiter.slug}
                style={{ padding: theme.spacing.elementPadding }}
              >
                <RecruiterCard recruiterData={recruiter} />
              </Grid.Column>
            ))}
          </Grid>
        </div>
      </main>
    );
  }

  return (
    <div>
      {content}
      <AppFooter forceShow={true} />
    </div>
  );
};

export default RecruiterListPage;
